if (!customElements.get('dc-product-visual-details')) {
  class DCProductVisualDetails extends HTMLElement {
    connectedCallback() {
      this.handleContextMenu = this.handleContextMenu || this.onContextMenu.bind(this);
      this.addEventListener('contextmenu', this.handleContextMenu);

      if (this.dataset.initialized === 'true') return;

      this.track = this.querySelector('[data-visual-track]');
      this.items = Array.from(this.querySelectorAll('[data-visual-item]'));
      this.previousButton = this.querySelector('[data-visual-previous]');
      this.nextButton = this.querySelector('[data-visual-next]');

      if (!this.track || this.items.length === 0) return;

      this.dataset.initialized = 'true';
      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.updateFrame = null;
      this.onPrevious = () => this.moveBy(-1);
      this.onNext = () => this.moveBy(1);
      this.onScroll = () => this.requestUpdate();
      this.onKeydown = (event) => this.handleKeydown(event);
      this.onResize = () => this.requestUpdate();

      this.previousButton?.addEventListener('click', this.onPrevious);
      this.nextButton?.addEventListener('click', this.onNext);
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      this.track.addEventListener('keydown', this.onKeydown);

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.onResize);
        this.resizeObserver.observe(this.track);
        this.items.forEach((item) => this.resizeObserver.observe(item));
      } else {
        window.addEventListener('resize', this.onResize, { passive: true });
      }

      this.querySelectorAll('img').forEach((image) => {
        if (!image.complete) image.addEventListener('load', this.onResize, { once: true });
      });

      requestAnimationFrame(() => this.update());
    }

    disconnectedCallback() {
      this.removeEventListener('contextmenu', this.handleContextMenu);
      this.previousButton?.removeEventListener('click', this.onPrevious);
      this.nextButton?.removeEventListener('click', this.onNext);
      this.track?.removeEventListener('scroll', this.onScroll);
      this.track?.removeEventListener('keydown', this.onKeydown);
      this.resizeObserver?.disconnect();
      if (!this.resizeObserver) window.removeEventListener('resize', this.onResize);
      if (this.updateFrame) cancelAnimationFrame(this.updateFrame);
    }

    onContextMenu(event) {
      const image = event.target?.closest?.('.dc-product-visual-details__image');

      if (image && this.contains(image)) {
        event.preventDefault();
      }
    }

    metrics() {
      const itemWidth = this.items[0]?.getBoundingClientRect().width || 0;
      const styles = window.getComputedStyle(this.track);
      const gap = Number.parseFloat(styles.columnGap) || Number.parseFloat(styles.gap) || 0;

      return { step: itemWidth + gap };
    }

    moveBy(direction) {
      const { step } = this.metrics();
      if (step <= 0) return;

      const maximumScroll = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      const currentIndex = Math.round(this.track.scrollLeft / step);
      const target = Math.min(maximumScroll, Math.max(0, (currentIndex + direction) * step));

      this.track.scrollTo({
        left: target,
        behavior: this.reduceMotion.matches ? 'auto' : 'smooth'
      });
    }

    handleKeydown(event) {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;

      event.preventDefault();
      this.moveBy(event.key === 'ArrowRight' ? 1 : -1);
    }

    requestUpdate() {
      if (this.updateFrame) cancelAnimationFrame(this.updateFrame);
      this.updateFrame = requestAnimationFrame(() => this.update());
    }

    update() {
      const tolerance = 2;
      const maximumScroll = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      const canScroll = maximumScroll > tolerance;
      const media = this.querySelector('.dc-product-visual-details__media');
      const carousel = this.querySelector('.dc-product-visual-details__carousel');

      this.classList.toggle('is-static', !canScroll);

      if (media && carousel) {
        const mediaRect = media.getBoundingClientRect();
        const carouselRect = carousel.getBoundingClientRect();
        this.style.setProperty('--dc-visual-media-center', `${mediaRect.top - carouselRect.top + (mediaRect.height / 2)}px`);
      }

      if (this.previousButton) {
        this.previousButton.hidden = !canScroll;
        this.previousButton.disabled = !canScroll || this.track.scrollLeft <= tolerance;
      }

      if (this.nextButton) {
        this.nextButton.hidden = !canScroll;
        this.nextButton.disabled = !canScroll || this.track.scrollLeft >= maximumScroll - tolerance;
      }

      this.updateFrame = null;
    }
  }

  customElements.define('dc-product-visual-details', DCProductVisualDetails);
}
